import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { Bell, MessageCircle, Check, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const NotificationRow = ({ notif, onClick }) => {
  const actor = notif.actor_display_name || notif.actor_username || 'Someone';
  const isUnread = !notif.is_read;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`notification-row-${notif.id}`}
      className={`w-full text-left flex gap-3 p-4 border-b border-zinc-800/60 transition-colors ${
        isUnread ? 'bg-orange-500/5 hover:bg-orange-500/10' : 'hover:bg-zinc-900/50'
      }`}
    >
      {/* Actor avatar OR fallback icon */}
      <div className="relative shrink-0">
        <div className="w-11 h-11 rounded-full bg-zinc-800 overflow-hidden flex items-center justify-center">
          {notif.actor_avatar ? (
            <img
              src={`${API}/api/files/${notif.actor_avatar}`}
              alt={actor}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <span className="text-base font-semibold text-white/80">{actor.charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center ring-2 ring-black">
          <MessageCircle className="w-3 h-3 text-white" />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white leading-snug">
          <span className="font-semibold">{actor}</span>
          <span className="text-zinc-400"> commented on </span>
          <span className="font-medium">{notif.video_title || 'your video'}</span>
        </p>
        {notif.comment_preview && (
          <p className="mt-1 text-sm text-zinc-300 line-clamp-2 italic">
            “{notif.comment_preview}”
          </p>
        )}
        <p className="mt-1 text-xs text-zinc-500">{timeAgo(notif.created_at)}</p>
      </div>

      {/* Thumbnail + unread dot */}
      <div className="shrink-0 flex flex-col items-end gap-2">
        {notif.video_thumbnail && (
          <div className="w-14 h-14 rounded-lg overflow-hidden bg-zinc-800">
            <img
              src={notif.video_thumbnail.startsWith('http') ? notif.video_thumbnail : `${API}${notif.video_thumbnail.startsWith('/') ? '' : '/'}${notif.video_thumbnail}`}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        )}
        {isUnread && <span className="w-2.5 h-2.5 rounded-full bg-orange-400" data-testid={`unread-dot-${notif.id}`} />}
      </div>
    </button>
  );
};

export default function NotificationsPage() {
  const { user, axiosInstance } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [prefs, setPrefs] = useState({ email_on_comment: true });
  const [savingPref, setSavingPref] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/notifications?limit=50');
      setItems(res.data.notifications || []);
    } catch (e) {
      toast.error('Could not load notifications.');
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  const loadPrefs = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/notifications/preferences');
      setPrefs(res.data || { email_on_comment: true });
    } catch (e) {
      // Non-critical; defaults remain
    }
  }, [axiosInstance]);

  useEffect(() => {
    if (!user) return;
    load();
    loadPrefs();
  }, [user, load, loadPrefs]);

  useEffect(() => {
    document.title = 'Notifications — AUDIVUE';
  }, []);

  const handleRowClick = async (notif) => {
    // Mark this one read (optimistically)
    if (!notif.is_read) {
      setItems((prev) => prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n)));
      try {
        await axiosInstance.post('/notifications/mark-read', { ids: [notif.id] });
        window.dispatchEvent(new CustomEvent('audivue:notifications:refresh'));
      } catch (e) {
        // revert on failure
        setItems((prev) => prev.map((n) => (n.id === notif.id ? { ...n, is_read: false } : n)));
      }
    }
    if (notif.video_id) navigate(`/video/${notif.video_id}`);
  };

  const handleMarkAllRead = async () => {
    setMarking(true);
    try {
      await axiosInstance.post('/notifications/mark-all-read');
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      window.dispatchEvent(new CustomEvent('audivue:notifications:refresh'));
      toast.success('All caught up.');
    } catch (e) {
      toast.error('Could not mark all as read.');
    } finally {
      setMarking(false);
    }
  };

  const handleTogglePref = async () => {
    const next = !prefs.email_on_comment;
    setPrefs({ ...prefs, email_on_comment: next });
    setSavingPref(true);
    try {
      await axiosInstance.put('/notifications/preferences', { email_on_comment: next });
      toast.success(next ? 'Email notifications on.' : 'Email notifications off.');
    } catch (e) {
      setPrefs({ ...prefs, email_on_comment: !next });
      toast.error('Could not update preference.');
    } finally {
      setSavingPref(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <Bell className="w-10 h-10 text-zinc-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2">Sign in to see your notifications</h1>
          <p className="text-sm text-zinc-400 mb-4">Replies, comments, and milestones from your fans show up here.</p>
          <Link to="/login" className="inline-block px-5 py-2.5 rounded-full bg-white text-black font-semibold hover:bg-zinc-200">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  const hasUnread = items.some((n) => !n.is_read);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-24" data-testid="notifications-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bell className="w-6 h-6 text-orange-400" />
              Notifications
            </h1>
            <p className="text-sm text-zinc-400 mt-1">When fans engage with your work, you&apos;ll see it here.</p>
          </div>
          {hasUnread && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={marking}
              data-testid="mark-all-read-btn"
              className="text-xs uppercase tracking-wider text-zinc-400 hover:text-white px-3 py-2 rounded-full border border-zinc-800 hover:border-zinc-600 transition-colors flex items-center gap-1.5"
            >
              {marking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Mark all read
            </button>
          )}
        </div>

        {/* Email pref toggle */}
        <div className="mb-4 p-3 rounded-xl border border-zinc-800 bg-zinc-900/40 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Mail className="w-4 h-4 text-zinc-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Email me when someone comments</p>
              <p className="text-xs text-zinc-500">Max 1 email per video per hour.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleTogglePref}
            disabled={savingPref}
            data-testid="email-pref-toggle"
            role="switch"
            aria-checked={prefs.email_on_comment}
            aria-label="Email me when someone comments"
            className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
              prefs.email_on_comment ? 'bg-orange-500' : 'bg-zinc-700'
            }`}
          >
            <span
              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                prefs.email_on_comment ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center" data-testid="notifications-empty">
            <Bell className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400">You&apos;re all caught up.</p>
            <p className="text-zinc-600 text-sm mt-1">No notifications yet.</p>
          </div>
        ) : (
          <div className="rounded-xl border border-zinc-800 overflow-hidden bg-zinc-950/50">
            {items.map((n) => (
              <NotificationRow key={n.id} notif={n} onClick={() => handleRowClick(n)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
